import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Link as LinkIcon,
  Key as KeyIcon,
  Language as LanguageIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Star as StarIcon,
  BugReport as BugReportIcon,
  Terminal as TerminalIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  Map as MapIcon,
  Launch as LaunchIcon,
  List as ListIcon,
  OpenInNew as OpenInNewIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import CloudflareLab from './CloudflareLab';

const Dashboard = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [purgeType, setPurgeType] = useState('surrogate_key');
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [urlTemplates, setUrlTemplates] = useState([
    '/sitemap.xml',
    '/robots.txt',
    '/favicon.ico',
    '/css/main.css',
    '/js/app.js'
  ]);
  const [customTemplate, setCustomTemplate] = useState('');
  const [rateLimitAction, setRateLimitAction] = useState(null);
  const [rateLimitSiteId, setRateLimitSiteId] = useState(null);
  const [, setRateLimitRemaining] = useState(0);
  const [rateLimitResetTime, setRateLimitResetTime] = useState(0);
  const [rateLimitNow, setRateLimitNow] = useState(Date.now());
  const [debugMode, setDebugMode] = useState(false);
  const [debugUrl, setDebugUrl] = useState('');
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  const [sitemapLoading, setSitemapLoading] = useState(false);
  const [sitemapResult, setSitemapResult] = useState(null);
  const [showSitemapUrls, setShowSitemapUrls] = useState(false);
  const [siteSearchOpen, setSiteSearchOpen] = useState(false);
  const [sitemapSubpath, setSitemapSubpath] = useState('');
  const [surrogateScanResult, setSurrogateScanResult] = useState(null);
  const [recentPurges, setRecentPurges] = useState([]);
  // What the edge was actually serving after the last URL purge, so "did it
  // work?" is answerable here instead of by eyeballing the live site.
  const [purgeVerification, setPurgeVerification] = useState(null);
  const [quickPurgingId, setQuickPurgingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    purgeLog: null,
    title: '',
    message: ''
  });
  const [wholeDomainDialog, setWholeDomainDialog] = useState({
    open: false,
    siteId: null,
    inputs: [],
    keys: [],
    wholeDomainKeys: []
  });
  const { isAdmin, isManager, isManagerOrAdmin, canAccessCloudflare } = useAuth();
  
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();
  const [activeTab, setActiveTab] = useState('fastly');

  useEffect(() => {
    // Load critical data immediately
    loadSites();
    loadRateLimitStatus();
    
    // Load URL templates from localStorage
    const savedTemplates = localStorage.getItem('urlTemplates');
    if (savedTemplates) {
      try {
      setUrlTemplates(JSON.parse(savedTemplates));
      } catch (error) {
        // Failed to load URL templates from localStorage
      }
    }
    
    // Defer non-critical data loading to avoid blocking initial render
    const timer = setTimeout(() => {
      loadRecentActivity();
      loadRecentPurges();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Check rate limit status from localStorage and start countdown if needed
  const loadRateLimitStatus = () => {
    const rateLimitData = localStorage.getItem('rateLimitData');
    if (rateLimitData) {
      try {
        const { action, siteId, resetTime, remaining } = JSON.parse(rateLimitData);
        const now = Date.now();
        
        if (resetTime > now) {
          setRateLimitAction(action || null);
          setRateLimitSiteId(siteId || null);
          setRateLimitResetTime(resetTime);
          setRateLimitRemaining(remaining);
        } else {
          // Rate limit has expired, clear it
          localStorage.removeItem('rateLimitData');
          setRateLimitRemaining(0);
          setRateLimitResetTime(0);
          setRateLimitAction(null);
          setRateLimitSiteId(null);
        }
      } catch (error) {
        // Failed to load rate limit data from localStorage
        localStorage.removeItem('rateLimitData');
      }
    }
  };

  // Countdown timer for rate limit
  useEffect(() => {
    if (rateLimitResetTime > 0) {
      const interval = setInterval(() => {
        const now = Date.now();
        setRateLimitNow(now); // tick each second to trigger re-render
        if (now >= rateLimitResetTime) {
          setRateLimitRemaining(0);
          setRateLimitResetTime(0);
          setRateLimitAction(null);
          setRateLimitSiteId(null);
          localStorage.removeItem('rateLimitData');
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [rateLimitResetTime]);

  // Save URL templates to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('urlTemplates', JSON.stringify(urlTemplates));
  }, [urlTemplates]);

  const loadSites = async () => {
    try {
      setSitesLoading(true);
      const data = await api.sites.list();
      setSites(data);
    } catch (error) {
      toast.error('Failed to load sites');
    } finally {
      setSitesLoading(false);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const data = await api.audit.list({ limit: 10 });
      setRecentActivity(data.logs || []);
    } catch (error) {
      // Failed to load recent activity
    }
  };

  const loadRecentPurges = async () => {
    try {
      const data = await api.audit.list({ 
        limit: 20, 
        action: ['purge_url', 'purge_surrogate_key', 'purge_all'] 
      });
      setRecentPurges(data.logs || []);
    } catch (error) {
      // Failed to load recent purges
    }
  };

  const selectedSiteData = sites.find(site => site.id === selectedSite);

  const handlePurgeError = (error) => {
    // Handle rate limit error from server
    if (error.statusCode === 429 && error.rateLimitInfo?.action === 'surrogate_key') {
      const { action, siteId, resetTime, remaining } = error.rateLimitInfo;
      setRateLimitAction(action || null);
      setRateLimitSiteId(siteId || selectedSite || null);
      setRateLimitResetTime(resetTime);
      setRateLimitRemaining(remaining);

      // Save to localStorage for persistence (only for surrogate-key)
      localStorage.setItem('rateLimitData', JSON.stringify({
        action: action || null,
        siteId: siteId || selectedSite || null,
        resetTime,
        remaining
      }));

      const remainingSeconds = Math.ceil((resetTime - Date.now()) / 1000);
      toast.error(`Rate limit exceeded. Please wait ${remainingSeconds} seconds before making another request.`);
    } else if (error.statusCode === 429) {
      // Fastly API token-level rate limit or other non-app limiter; do not persist
      toast.error('Fastly API rate limit hit for URL purge. Try again shortly.');
    } else {
      toast.error(error.message || 'Purge operation failed');
    }
  };

  // Sends the pasted URLs for key-based purging. Resolves false when the server
  // asks for confirmation because one of the keys would purge a whole domain.
  const runUrlPurge = async (siteId, inputs, { confirmWholeDomain = false } = {}) => {
    try {
      const result = await api.purge.url(siteId, inputs, { confirmWholeDomain });
      const keys = result?.keys || [];
      setPurgeVerification(
        result?.verification
          ? { ...result.verification, keys, message: result.message, at: Date.now() }
          : null
      );
      // The server waits for the origin to finish regenerating, so its message
      // describes what the edge actually ended up with -- prefer it over a
      // generic "purged" that would be true even when the content is stale.
      if (result?.message) {
        toast.success(result.message);
      } else if (keys.length === 1) {
        toast.success(`Purged: ${keys[0]}`);
      } else {
        toast.success(`Purged ${keys.length} keys: ${keys.join(', ')}`);
      }
      return true;
    } catch (error) {
      if (error.statusCode === 409 && error.data?.requiresWholeDomainConfirmation) {
        setWholeDomainDialog({
          open: true,
          siteId,
          inputs,
          keys: error.data.keys || [],
          wholeDomainKeys: error.data.wholeDomainKeys || []
        });
        return false;
      }
      throw error;
    }
  };

  const handleConfirmWholeDomainPurge = async () => {
    const { siteId, inputs } = wholeDomainDialog;
    setWholeDomainDialog((prev) => ({ ...prev, open: false }));

    setLoading(true);
    try {
      const purged = await runUrlPurge(siteId, inputs, { confirmWholeDomain: true });
      if (purged) {
        reset();
        loadRecentActivity();
        loadRecentPurges();
      }
    } catch (error) {
      handlePurgeError(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!selectedSite || !purgeType) {
      toast.error('Please select a site and purge type');
      return;
    }

    // Check rate limit before making request (only for surrogate-key app limiter)
    const now = Date.now();
    if (
      purgeType === 'surrogate_key' &&
      rateLimitAction === 'surrogate_key' &&
      rateLimitSiteId === selectedSite &&
      rateLimitResetTime > now
    ) {
      const remainingSeconds = Math.ceil((rateLimitResetTime - now) / 1000);
      toast.error(`Rate limited. Please wait ${remainingSeconds} seconds before making another purge request.`);
      return;
    }

    setLoading(true);
    // Drop the previous readout immediately; leaving it on screen during a new
    // purge would show a result that no longer describes anything.
    setPurgeVerification(null);
    let purged = true;
    try {
      switch (purgeType) {
        case 'url': {
          // One URL per line; the server derives a surrogate key from each.
          const inputs = String(data.url || '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
          if (inputs.length === 0) {
            toast.error('URL is required');
            return;
          }
          purged = await runUrlPurge(selectedSite, inputs);
          break;
        }
          
        case 'surrogate_key':
          if (!data.surrogateKey) {
            toast.error('Surrogate key is required');
            return;
          }
          await api.purge.surrogateKey(selectedSite, data.surrogateKey);
          toast.success(`Full site purged for key: ${data.surrogateKey}`);
          break;
          
        case 'purge_all':
          await api.purge.all(selectedSite);
          toast.success('All cache purged successfully');
          break;
          
        default:
          toast.error('Invalid purge type');
          return;
      }
      
      // Leave the form intact when the purge is waiting on confirmation
      if (!purged) return;

      reset();
      loadRecentActivity();
      loadRecentPurges(); // Refresh recent purges
    } catch (error) {
      handlePurgeError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDebugHeaders = async () => {
    const url = (debugUrl || watch('url') || '').trim();
    if (!selectedSite || !url) {
      toast.error('Please select a site and enter a URL path');
      return;
    }

    setDebugLoading(true);
    setDebugResult(null);
    
    try {
      const result = await api.purge.debugHeaders(selectedSite, url);
      setDebugResult(result);
      // Auto-open the headers panel when results arrive
      setDebugMode(true);
      toast.success('Debug headers fetched successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to fetch debug headers');
      setDebugResult({ 
        error: error.message || 'Failed to fetch debug headers',
        url: `https://${selectedSiteData?.domain}${url}`,
        curlCommand: `curl -I "https://${selectedSiteData?.domain}${url}"`
      });
      // Ensure the panel is visible for error details
      setDebugMode(true);
    } finally {
      setDebugLoading(false);
    }
  };

  const handleScanSitemap = async () => {
    if (!selectedSite) {
      toast.error('Please select a site first');
      return;
    }

    setSitemapLoading(true);
    setSitemapResult(null);
    
    try {
      const result = await api.purge.scanSitemap(selectedSite, sitemapSubpath);
      setSitemapResult(result);
      if (result.found) {
        let message = result.subSitemapsCount > 0 
          ? `Found ${result.totalUrls} URLs from ${result.subSitemapsCount + 1} sitemap${result.subSitemapsCount > 0 ? 's' : ''}`
          : `Found ${result.totalUrls} URLs in sitemap`;
        
        if (result.failedCount > 0) {
          message += ` (${result.failedCount} failed)`;
          toast.warn(message);
        } else {
          toast.success(message);
        }
        setShowSitemapUrls(true);
      } else {
        toast.warn('No sitemap found at common locations');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to scan sitemap');
      setSitemapResult({ 
        found: false,
        error: error.message || 'Failed to scan sitemap',
        domain: selectedSiteData?.domain
      });
    } finally {
      setSitemapLoading(false);
    }
  };

  const handleQuickPurge = async (purgeLog) => {
    if (!purgeLog.site_id) {
      toast.error('Site information not available for this purge log');
      return;
    }

    // Show confirmation dialog
    const getPurgeDescription = (action, target, siteName) => {
      if (action === 'purge_url') {
        return `URL: ${target}`;
      } else if (action === 'purge_surrogate_key') {
        return `Surrogate Key: ${target}`;
      } else if (action === 'purge_all') {
        return `All content for ${siteName}`;
      }
      return target || 'all content';
    };

    setConfirmDialog({
      open: true,
      purgeLog: purgeLog,
      title: 'Confirm Purge Again',
      message: `Are you sure you want to purge again?\n\n${getPurgeDescription(purgeLog.action, purgeLog.target, purgeLog.site_name)}`
    });
  };

  const handleConfirmPurge = async () => {
    const purgeLog = confirmDialog.purgeLog;
    if (!purgeLog) return;

    setQuickPurgingId(purgeLog.id);
    setConfirmDialog({ open: false, purgeLog: null, title: '', message: '' });

    try {
      // Set the site and purge type
      setSelectedSite(purgeLog.site_id);
      // Extract purge type from action (e.g., 'purge_surrogate_key' -> 'surrogate_key')
      const purgeType = purgeLog.action?.replace('purge_', '') || 'surrogate_key';
      setPurgeType(purgeType);

      // Set the appropriate form values based on purge type
      if (purgeLog.action === 'purge_url' && purgeLog.target) {
        setValue('url', purgeLog.target);
      } else if (purgeLog.action === 'purge_surrogate_key' && purgeLog.target) {
        setValue('surrogateKey', purgeLog.target);
      }

            // Execute the purge immediately
      let result;
      try {
        
        
        if (purgeLog.action === 'purge_url') {
          // Newer entries store space-separated surrogate keys; older ones store a
          // single full URL. Splitting on whitespace handles both.
          const targets = String(purgeLog.target || '').trim().split(/\s+/).filter(Boolean);
          if (targets.length === 0) {
            toast.error('No URL recorded for this purge log');
            return;
          }
          if (await runUrlPurge(purgeLog.site_id, targets)) {
            loadRecentActivity();
            loadRecentPurges();
          }
          return;
        }

        if (purgeLog.action === 'purge_surrogate_key') {
          result = await api.purge.surrogateKey(purgeLog.site_id, purgeLog.target);
        } else if (purgeLog.action === 'purge_all') {
          result = await api.purge.all(purgeLog.site_id);
        }
        
        if (result && result.success) {
          toast.success(`Cache double-purged successfully! ${purgeLog.action?.replace('_', ' ')} - ${purgeLog.target || 'all content'}`);
          // Refresh the lists
          loadRecentActivity();
          loadRecentPurges();
        } else {
          toast.error(`Purge failed: ${result?.message || result?.error || 'Unknown error'}`);
        }
      } catch (apiError) {
        // Handle API errors specifically
        console.error('API call failed:', apiError);
        
        if (apiError.status === 429) {
          toast.error('Rate limit exceeded. Please wait before trying again.');
        } else if (apiError.status === 403) {
          toast.error('Access denied. Check your permissions.');
        } else if (apiError.status === 404) {
          toast.error('Site or resource not found.');
        } else {
          toast.error(`Purge failed: ${apiError.message || 'Network or server error'}`);
        }
      }

    } catch (error) {
      console.error('Quick purge error:', error);
      toast.error(`Purge failed: ${error.message || 'Unknown error'}`);
    } finally {
      setQuickPurgingId(null);
    }
  };

  const handleScanSurrogateKey = async () => {
    if (!selectedSite) {
      toast.error('Please select a site first');
      return;
    }

    setSurrogateScanResult(null);
    
    try {
      // Scan the site's root URL for surrogate keys
      const result = await api.purge.scanSurrogateKey(selectedSite);
      
      if (result.surrogateKeyFound) {
        // Auto-apply key without showing the success prompt panel
        setValue('surrogateKey', result.surrogateKey);
        setSurrogateScanResult(null);
      } else {
        // Show informational panel only when not found
        setSurrogateScanResult(result);
        toast.info('No surrogate key found in site headers. Check your Fastly configuration.');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to scan for surrogate keys');
      setSurrogateScanResult({ 
        error: error.message || 'Failed to scan for surrogate keys',
        url: `https://${selectedSiteData?.domain}/`
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Explain Fastly x-cache header patterns (supports 1, 2, or 3 values)
  // Optionally enrich with x-served-by POP codes.
  // Returns { header: string, lines: string[], flow: {label:string,value:string,pop?:string}[], summary: string }
  const getXCacheExplanation = (xCacheRaw, xServedByRaw) => {
    if (!xCacheRaw || typeof xCacheRaw !== 'string') return null;
    const tokens = xCacheRaw.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);
    if (tokens.length === 0) return null;

    const parsePops = (raw) => {
      if (!raw || typeof raw !== 'string') return [];
      return raw.split(',').map(s => s.trim()).map(s => {
        const parts = s.split('-');
        const last = parts[parts.length - 1] || '';
        const code = last.replace(/[^A-Z]/g, '').toUpperCase();
        return code || null;
      }).filter(Boolean);
    };
    const pops = parsePops(xServedByRaw);

    const explainPair = (a, b) => {
      if (a === 'MISS' && b === 'MISS') {
        return 'The object was not in cache at either the edge or the shield. It was fetched from the backend (counts as two misses).';
      }
      if (a === 'HIT' && b === 'MISS') {
        return 'The object was not in cache at the edge, so it was forwarded to the shield where it was found in cache (1 hit, 1 miss).';
      }
      if (a === 'MISS' && b === 'HIT') {
        return "The object was found in cache at the edge. The 'MISS' is a prior event at the shield, not something that happened in this request.";
      }
      if (a === 'HIT' && b === 'HIT') {
        return "The object was found in cache at the edge. When it was previously fetched from the shield, it was a HIT at the shield.";
      }
      return 'Unrecognized x-cache pattern.';
    };

    const summarizePair = (a, b) => {
      if (a === 'MISS' && b === 'MISS') return 'Not found at shield or edge; served directly from origin.';
      if (a === 'HIT' && b === 'MISS') return 'Edge miss; served from shield cache (no origin request).';
      if (a === 'MISS' && b === 'HIT') return 'Served from edge cache (no origin request).';
      if (a === 'HIT' && b === 'HIT') return 'Served from edge cache; shield also had a cached copy.';
      return 'Pattern not recognized.';
    };

    // 3 values → NGWAF present; first value is WAF outcome, last two are (shield, edge)
    if (tokens.length === 3) {
      const waf = tokens[0];
      const shield = tokens[1];
      const edge = tokens[2];
      return {
        header: `${tokens.join(', ')}`,
        lines: [
          `NGWAF: ${waf} (first value). When a request is allowed, this is typically MISS at the WAF layer.`,
          explainPair(shield, edge)
        ],
        flow: [
          { label: 'WAF', value: waf, pop: 'NGWAF' },
          { label: 'Shield', value: shield, pop: pops[1] || pops[pops.length - 1] || '' },
          { label: 'Edge', value: edge, pop: pops[0] || '' },
        ],
        summary: summarizePair(shield, edge)
      };
    }

    // 2 values → (shield, edge)
    if (tokens.length === 2) {
      return {
        header: `${tokens.join(', ')}`,
        lines: [explainPair(tokens[0], tokens[1])],
        flow: [
          { label: 'Shield', value: tokens[0], pop: pops[0] || '' },
          { label: 'Edge', value: tokens[1], pop: pops[1] || pops[0] || '' },
        ],
        summary: summarizePair(tokens[0], tokens[1])
      };
    }

    // 1 value → POP that received the request is the designated shield
    if (tokens.length === 1) {
      const v = tokens[0];
      if (v === 'HIT') {
        return {
          header: v,
          lines: ['The object was found in cache, and the POP that received the request is the designated shield. The object was originally loaded directly from the backend.'],
          flow: [ { label: 'Designated Shield POP', value: v, pop: pops[0] || '' } ],
          summary: 'Served from cache at the shield POP.'
        };
      }
      if (v === 'MISS') {
        return {
          header: v,
          lines: ['The object was not in cache, and the POP that received the request is the designated shield. The object was fetched directly from the backend.'],
          flow: [ { label: 'Designated Shield POP', value: v, pop: pops[0] || '' } ],
          summary: 'Backend fetch occurred at the shield POP.'
        };
      }
    }

    return null;
  };

  // Build an ASCII flow from the parsed flow steps
  // Example flow: [{label:'WAF', value:'MISS'}, {label:'Shield', value:'HIT'}, {label:'Edge', value:'HIT'}]
  const buildAsciiFlow = (flow) => {
    if (!Array.isArray(flow) || flow.length === 0) return '';
    const steps = ['Request', ...flow.map(s => `${s.label} (${s.value})`)];
    // Vertical diagram:
    // Request
    //   |
    //   v
    // Shield (HIT)
    //   |
    //   v
    // Edge (HIT)
    return steps.map((t, i) => (i === 0 ? t : `  |\n  v\n${t}`)).join('\n');
  };

  // Build a horizontal boxed ASCII diagram with arrows between nodes
  // Example:
  // +--------------------+  ->  +--------------------+
  // |       NGWAF       |      |       SHIELD       |
  // |       [MISS]      |      |        [HIT]       |
  // +--------------------+      +--------------------+
  const buildAsciiBoxFlow = (flow) => {
    if (!Array.isArray(flow) || flow.length === 0) return '';
    const width = 22; // inner width for text centering
    const center = (text) => {
      const t = String(text);
      const pad = Math.max(0, width - t.length);
      const left = Math.floor(pad / 2);
      const right = pad - left;
      return ' '.repeat(left) + t + ' '.repeat(right);
    };
    const top = '+' + '-'.repeat(width) + '+';
    const bottom = top;
    const boxes = flow.map(step => {
      return [
        top,
        '|' + center((step.pop || step.label).toString().toUpperCase()) + '|',
        '|' + center('[' + step.label.toUpperCase() + ']') + '|',
        '|' + center(step.label === 'Edge' ? 'Fastly POP' : (step.label === 'Shield' ? 'Shield POP' : 'WAF')) + '|',
        bottom
      ];
    });
    const arrow = '  ->  ';
    const lines = [0,1,2,3,4].map(row => boxes.map(b => b[row]).join(arrow));
    // Include a small legend row above for the initial Request node
    const requestBox = [
      top,
      '|' + center('REQUEST') + '|',
      '|' + center('') + '|',
      '|' + center('') + '|',
      bottom
    ];
    const withRequest = [0,1,2,3,4].map(row => requestBox[row] + (boxes.length ? arrow : '') + (boxes.length ? boxes.map(b => b[row]).join(arrow) : '') );
    return withRequest.join('\n');
  };

  // Appends to the multiline URL field so several targets can be queued up.
  const appendUrlLine = (urlPath) => {
    const lines = String(watch('url') || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.includes(urlPath)) return false;
    setValue('url', [...lines, urlPath].join('\n'));
    return true;
  };

  const applySitemapUrl = (urlPath) => {
    if (appendUrlLine(urlPath)) {
      toast.success(`URL added: ${urlPath}`);
    } else {
      toast.success(`Already in the list: ${urlPath}`);
    }
  };

  const visitPage = (urlPath) => {
    if (!selectedSiteData) return;
    const fullUrl = `https://${selectedSiteData.domain}${urlPath}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
    toast.success(`Opening: ${fullUrl}`);
  };

  const exportToCSV = () => {
    if (!sitemapResult || !sitemapResult.urls || sitemapResult.urls.length === 0) {
      toast.error('No URLs to export');
      return;
    }

    // Prepare CSV data
    const headers = ['URL Path', 'Full URL', 'Last Modified'];
    const csvData = [headers];
    
    sitemapResult.urls.forEach(urlData => {
      csvData.push([
        urlData.path,
        `https://${selectedSiteData.domain}${urlData.path}`,
        urlData.lastmod || 'Not specified'
      ]);
    });

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => 
        // Escape quotes and wrap fields containing commas in quotes
        field.includes(',') || field.includes('"') 
          ? `"${field.replace(/"/g, '""')}"` 
          : field
      ).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sitemap-urls-${selectedSiteData.domain}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${sitemapResult.urls.length} URLs to CSV`);
  };

  const handleSiteChange = (siteId) => {
    setSelectedSite(siteId);
    setPurgeType('surrogate_key'); // Reset to default
    reset();
    setSurrogateScanResult(null);
  };

  const addCustomTemplate = () => {
    if (customTemplate && !urlTemplates.includes(customTemplate)) {
      setUrlTemplates(prev => [...prev, customTemplate]);
      setCustomTemplate('');
      toast.success('URL template added to favorites');
    }
  };

  const removeTemplate = (template) => {
    setUrlTemplates(prev => prev.filter(t => t !== template));
    toast.success('URL template removed from favorites');
  };

  const applyTemplate = (template) => {
    appendUrlLine(template);
  };

  const renderPurgeForm = () => {
    if (!selectedSite || !purgeType) return null;

    const site = sites.find(s => s.id === selectedSite);
    if (!site?.purge_types.includes(purgeType)) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          This purge type is not allowed for the selected site.
        </Alert>
      );
    }

    switch (purgeType) {
      case 'url':
        return (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={10}
              label="URLs to Purge (one per line)"
              placeholder={watch('url') ? undefined : "https://example.com/about  or  /about\nOne URL per line to purge several at once"}
              value={watch('url') || ''}
              onChange={(e) => setValue('url', e.target.value)}
              sx={{ mt: 2 }}
              {...register('url', {
                required: 'URL is required',
                validate: (value) => {
                  const lines = String(value || '')
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean);
                  if (lines.length === 0) return 'URL is required';
                  if (lines.length > 100) return 'Purge at most 100 URLs at a time';
                  return true;
                },
              })}
              error={!!errors.url}
              helperText={
                errors.url?.message ||
                'Paste full URLs or paths, one per line. Query strings are ignored, so purging a page also clears its query-string and language variants. A bare domain purges the entire site and will ask for confirmation.'
              }
            />
            
            {/* URL Templates/Favorites */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <StarIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                URL Templates (Favorites)
              </Typography>
              
              {/* Quick access for root */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  label="/ (Root)"
                  size="small"
                  onClick={() => applyTemplate('/')}
                  sx={{ cursor: 'pointer' }}
                  color="secondary"
                  variant="filled"
                />
                {urlTemplates.map((template, index) => (
                  <Chip
                    key={index}
                    label={template}
                    size="small"
                    onClick={() => applyTemplate(template)}
                    onDelete={() => removeTemplate(template)}
                    deleteIcon={<CloseIcon />}
                    sx={{ cursor: 'pointer' }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add new template (e.g., /api/cache)"
                  value={customTemplate}
                  onChange={(e) => setCustomTemplate(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomTemplate();
                    }
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addCustomTemplate}
                  disabled={!customTemplate || urlTemplates.includes(customTemplate)}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        );
        
      case 'surrogate_key':
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Full Site"
              placeholder="Enter full-site header value (e.g., domain.com, header-v1)"
              value={watch('surrogateKey') || ''}
              onChange={(e) => setValue('surrogateKey', e.target.value)}
              {...register('surrogateKey', {
                required: 'Value is required',
                minLength: {
                  value: 1,
                  message: 'Value cannot be empty',
                },
              })}
              error={!!errors.surrogateKey}
              helperText={errors.surrogateKey?.message || 'Enter the header value used to purge the full site (commonly the domain name)'}
            />
            
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Get full site header value from response headers:
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleScanSurrogateKey}
                startIcon={<SearchIcon />}
              >
                Get Full Site Header Value
              </Button>
            </Box>
            
            {surrogateScanResult && (
              <Alert 
                severity={surrogateScanResult.surrogateKeyFound ? "success" : "info"} 
                sx={{ mt: 2 }}
              >
                <Typography variant="body2">
                  {surrogateScanResult.surrogateKeyFound ? (
                    <>
                      <strong>Header Value Found!</strong><br />
                      Scanned: <code>{surrogateScanResult.url}</code><br />
                      Header Value: <code>{surrogateScanResult.surrogateKey}</code><br />
                      {surrogateScanResult.surrogateControl && (
                        <>Control: <code>{surrogateScanResult.surrogateControl}</code><br /></>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ mt: 1 }}
                        onClick={() => {
                          setValue('surrogateKey', surrogateScanResult.surrogateKey);
                          toast.success(`Header value "${surrogateScanResult.surrogateKey}" copied to form!`);
                        }}
                      >
                        Use This Value
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1, ml: 1 }}
                        onClick={() => {
                          navigator.clipboard.writeText(surrogateScanResult.surrogateKey);
                          toast.success('Header value copied to clipboard!');
                        }}
                      >
                        Copy to Clipboard
                      </Button>
                    </>
                  ) : (
                    <>
                      <strong>No Header Value Found</strong><br />
                      Scanned: <code>{surrogateScanResult.url}</code><br />
                      No surrogate key headers found. Check your Fastly configuration or try scanning a different page.
                      {surrogateScanResult.allHeaders && (
                        <>
                          <br /><br />
                          <strong>Debug Info:</strong><br />
                          <details>
                            <summary>Click to see all response headers</summary>
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: '0.8rem' }}>
                              {Object.entries(surrogateScanResult.allHeaders).map(([key, value]) => (
                                <div key={key}><strong>{key}:</strong> {value}</div>
                              ))}
                            </Box>
                          </details>
                        </>
                      )}
                    </>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        );
        
      case 'purge_all':
        return (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This will purge all cached content for {selectedSiteData?.domain}.
              This action cannot be undone.
            </Typography>
          </Alert>
        );
        
      default:
        return null;
    }
  };

  const getPurgeTypeIcon = (type) => {
    // Handle both full action names and purge type names
    const purgeType = type?.replace('purge_', '') || type;
    
    switch (purgeType) {
      case 'url':
        return <LinkIcon />;
      case 'surrogate_key':
        return <KeyIcon />;
      case 'all':
        return <DeleteIcon />;
      default:
        return <SpeedIcon />;
    }
  };

  const getStatusIcon = (status) => {
    return status === 'success' ? (
      <CheckCircleIcon color="success" fontSize="small" />
    ) : (
      <ErrorIcon color="error" fontSize="small" />
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Cache Purge Dashboard
      </Typography>
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        textColor="primary"
        indicatorColor="primary"
        sx={{ 
          mb: 2,
          '& .MuiTabs-indicator': { height: 4, borderRadius: 2 }
        }}
      >
        <Tab 
          label="Fastly" 
          value="fastly" 
          sx={{ fontWeight: 700, textTransform: 'uppercase', '&.Mui-selected': { color: 'primary.main' } }}
        />
        {canAccessCloudflare && (
          <Tab 
            label="Cloudflare" 
            value="cloudflare" 
            sx={{ fontWeight: 700, textTransform: 'uppercase', '&.Mui-selected': { color: 'primary.main' } }}
          />
        )}
      </Tabs>
      
      {activeTab === 'cloudflare' && canAccessCloudflare && (
        <CloudflareLab />
      )}
      
      {activeTab === 'fastly' && (
        <>
      
      <Grid container spacing={3}>
        {/* Main Purge Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Purge Cache
              </Typography>
              
              <Box component="form" onSubmit={handleSubmit(onSubmit)} data-purge-form>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={sites}
                      getOptionLabel={(option) => option.domain}
                      value={sites.find(site => site.id === selectedSite) || null}
                      onChange={(event, newValue) => {
                        handleSiteChange(newValue ? newValue.id : '');
                      }}
                      disabled={sitesLoading}
                      loading={sitesLoading}
                      open={siteSearchOpen}
                      onOpen={() => setSiteSearchOpen(true)}
                      onClose={() => setSiteSearchOpen(false)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Site (Searchable)"
                          placeholder="Type to search domains..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {sitesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Box 
                              component="img" 
                              src="/EeI5LUnN_400x400.jpg" 
                              alt="Fastly" 
                              sx={{ width: 20, height: 20, borderRadius: '4px' }} 
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body1">{option.domain}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.name}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      noOptionsText={sitesLoading ? "Loading sites..." : "No sites found"}
                      filterOptions={(options, { inputValue }) => {
                        return options.filter(option => 
                          option.domain.toLowerCase().includes(inputValue.toLowerCase()) ||
                          option.name.toLowerCase().includes(inputValue.toLowerCase())
                        );
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={!selectedSite}>
                      <InputLabel>Purge Type</InputLabel>
                      <Select
                        value={purgeType}
                        onChange={(e) => setPurgeType(e.target.value)}
                        label="Purge Type"
                      >
                        {selectedSiteData?.purge_types
                          .filter(type => isAdmin || type !== 'purge_all') // Only admins can see purge_all
                          .map((type) => (
                          <MenuItem key={type} value={type}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getPurgeTypeIcon(type)}
                              {type === 'url' && 'URL Specific'}
                              {type === 'surrogate_key' && 'Full Site'}
                              {type === 'purge_all' && 'Purge All'}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                {purgeType && (
                  <Box sx={{ mt: 2 }}>
                    {purgeType === 'url' && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>URL Purging:</strong> Clears specific pages on {selectedSiteData?.domain} using the
                          per-page surrogate key the CDN tags each object with. Paste full URLs or paths, one per line.
                          <br /><br />
                          <strong>💡 Query strings are ignored</strong> — purging <code>/slots/</code> also clears every
                          query-string, geo and language variant of that page in one shot. Trailing slashes don't matter.
                        </Typography>
                      </Alert>
                    )}
                    
                    {purgeType === 'surrogate_key' && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Full Site Purging:</strong> Purges all pages tagged with a specific header value (commonly the domain key). This is efficient for bulk purging.
                          <br /><br />
                          <strong>⏳ Note:</strong> Purging the full site has a cooldown (rate limit). You can still purge individual URLs while waiting.
                          <br /><br />
                          <strong>💡 Tip:</strong> Click "Get Full Site Header Value" to detect it from your site's response headers.
                        </Typography>
                      </Alert>
                    )}
                    
                    {purgeType === 'purge_all' && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Purge All:</strong> ⚠️ Removes ALL cached content for ALL domains 
                          in this Fastly service. This affects every website/domain configured in the 
                          service, not just {selectedSiteData?.domain}. Use with caution!
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
                
                {renderPurgeForm()}
                
                {selectedSite && purgeType && (
                  <>
                    
                    {purgeType === 'surrogate_key' && rateLimitAction === 'surrogate_key' && rateLimitSiteId === selectedSite && rateLimitResetTime > Date.now() && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Rate Limited:</strong> Please wait {Math.max(0, Math.ceil((rateLimitResetTime - rateLimitNow) / 1000))} seconds before making another surrogate-key purge.
                          <br />You can still purge a single URL while you wait.
                        </Typography>
                      </Alert>
                    )}
                    
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      sx={{ mt: 3 }}
                      disabled={loading || (purgeType === 'surrogate_key' && rateLimitAction === 'surrogate_key' && rateLimitSiteId === selectedSite && rateLimitResetTime > Date.now())}
                      startIcon={loading ? <CircularProgress size={20} /> : getPurgeTypeIcon(purgeType)}
                    >
                      {loading ? 'Purging...' : (purgeType === 'surrogate_key' && rateLimitAction === 'surrogate_key' && rateLimitSiteId === selectedSite && rateLimitResetTime > Date.now()) ? 'Rate Limited' : 'Execute Purge'}
                    </Button>
                  </>
                )}
              </Box>
              
              {selectedSiteData && (
                <Paper sx={{ mt: 3, p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Site Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Related Service:</strong> {selectedSiteData.name}<br />
                    <strong>Domain:</strong> {selectedSiteData.domain.startsWith('www.') ? 
                      `${selectedSiteData.domain} (with WWW)` : 
                      selectedSiteData.domain
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Allowed Purge Types:</strong>
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {selectedSiteData.purge_types
                      .filter(type => isAdmin || type !== 'purge_all') // Only admins can see purge_all
                      .map((type) => (
                      <Chip 
                        key={type} 
                        label={type} 
                        size="small" 
                      />
                    ))}
                  </Box>
                </Paper>
              )}
              
              {/* Debug Section for Managers and Admins */}
              {(isAdmin || isManager) && (
                <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.900', color: 'lime' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'lime', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugReportIcon fontSize="small" />
                    Debug Tools (Cache Headers Check)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', mb: 2, display: 'block' }}>
                    💡 Tip: For detailed analytics and statistics, visit the "Audit Logs" page and click "Show Stats"
                  </Typography>
                  
                  {!selectedSiteData && (
                    <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(46, 125, 50, 0.1)', color: 'lime', '& .MuiAlert-icon': { color: 'lime' } }}>
                      Select a site and enter a URL path above to use the debug tools
                    </Alert>
                  )}
                  
                  {/* Sitemap Subpath Input */}
                  {selectedSiteData && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        size="small"
                        label="Sitemap Subpath (Optional)"
                        placeholder="e.g., uk, fr, blog (leave empty for root)"
                        value={sitemapSubpath}
                        onChange={(e) => setSitemapSubpath(e.target.value)}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="body2" sx={{ color: '#888' }}>
                                {selectedSiteData.domain}/
                              </Typography>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography variant="body2" sx={{ color: '#888' }}>
                                /sitemap.xml
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            '& fieldset': { borderColor: '#444' },
                            '&:hover fieldset': { borderColor: '#666' },
                            '&.Mui-focused fieldset': { borderColor: 'cyan' },
                          },
                          '& .MuiInputLabel-root': { color: '#888' },
                          '& .MuiInputLabel-root.Mui-focused': { color: 'cyan' },
                          '& .MuiInputBase-input': { color: 'lime' },
                        }}
                        helperText={
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            For country/language specific sitemaps like domain.com/uk/sitemap.xml
                          </Typography>
                        }
                      />
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="URL Path"
                      placeholder="/path/to/page"
                      value={debugUrl}
                      onChange={(e) => setDebugUrl(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" sx={{ color: '#888' }}>
                              {selectedSiteData?.domain}
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        minWidth: 280,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          '& fieldset': { borderColor: '#444' },
                          '&:hover fieldset': { borderColor: '#666' },
                          '&.Mui-focused fieldset': { borderColor: 'cyan' },
                        },
                        '& .MuiInputLabel-root': { color: '#888' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'cyan' },
                        '& .MuiInputBase-input': { color: 'lime' },
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleDebugHeaders}
                      disabled={debugLoading || !(debugUrl || watch('url')) || !selectedSite}
                      startIcon={debugLoading ? <CircularProgress size={16} /> : <TerminalIcon />}
                      sx={{ 
                        borderColor: 'lime', 
                        color: 'lime',
                        '&:hover': { borderColor: 'lightgreen', color: 'lightgreen' },
                        '&:disabled': { borderColor: '#666', color: '#666' }
                      }}
                    >
                      {!selectedSite ? 'Select Site First' : !(debugUrl || watch('url')) ? 'Enter URL Path' : 'Show Headers'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setDebugMode(!debugMode)}
                      startIcon={<VisibilityIcon />}
                      sx={{ 
                        borderColor: 'lime', 
                        color: 'lime',
                        '&:hover': { borderColor: 'lightgreen', color: 'lightgreen' }
                      }}
                    >
                      {debugMode ? 'Hide' : 'Show'} Headers Panel
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleScanSitemap}
                      disabled={sitemapLoading || !selectedSite}
                      startIcon={sitemapLoading ? <CircularProgress size={16} /> : <MapIcon />}
                      sx={{ 
                        borderColor: 'cyan', 
                        color: 'cyan',
                        '&:hover': { borderColor: 'lightcyan', color: 'lightcyan' },
                        '&:disabled': { borderColor: '#666', color: '#666' }
                      }}
                    >
                      {!selectedSite ? 'Select Site First' : 'Scan Sitemap'}
                    </Button>
                  </Box>
                  
                  {debugMode && debugResult && (
                    <Paper sx={{ 
                      p: 2, 
                      bgcolor: '#000', 
                      color: 'lime', 
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      border: '1px solid lime',
                      borderRadius: 1
                    }}>
                      {debugResult.error ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#ff6b6b', mb: 1 }}>
                            ERROR: {debugResult.error}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'lime', mb: 1 }}>
                            URL: {debugResult.url}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <Typography variant="body2" sx={{ color: 'lime' }}>
                              Debug Command:
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => copyToClipboard(debugResult.curlCommand)}
                              startIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                              sx={{ 
                                color: 'lime', 
                                fontSize: '0.7rem',
                                minWidth: 'auto',
                                p: 0.5
                              }}
                            >
                              Copy
                            </Button>
                          </Box>
                          <pre style={{ margin: 0, color: 'lime', background: 'transparent' }}>
                            {debugResult.curlCommand}
                          </pre>
                        </Box>
                      ) : (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'lime' }}>
                              Cache Headers Analysis - {debugResult.url}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'orange' }}>
                              Status: {debugResult.statusCode} {debugResult.statusText}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'yellow', mb: 1 }}>
                              CACHE HEADERS:
                            </Typography>
                            {Object.entries(debugResult.cacheHeaders).map(([key, value]) => (
                              <Box key={key} sx={{ display: 'flex', mb: 0.5 }}>
                                <Typography sx={{ color: 'cyan', minWidth: '180px' }}>
                                  {key}:
                                </Typography>
                                <Typography sx={{ 
                                  color: value === 'Not found' ? '#666' : 'lime',
                                  fontWeight: value.includes('HIT') ? 'bold' : 'normal'
                                }}>
                                  {value}
                                </Typography>
                              </Box>
                            ))}
                            {/* x-cache explanation */}
                            {debugResult.cacheHeaders && debugResult.cacheHeaders['x-cache'] && (() => {
                              const exp = getXCacheExplanation(
                                debugResult.cacheHeaders['x-cache'],
                                debugResult.cacheHeaders['x-served-by']
                              );
                              if (!exp) return null;
                              return (
                                <Box sx={{ mt: 1, p: 1.5, border: '1px dashed #444', borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                                  <Typography variant="body2" sx={{ color: 'cyan', mb: 0.5 }}>
                                    x-cache interpretation: {exp.header}
                                  </Typography>
                                  {exp.lines.map((line, idx) => (
                                    <Typography key={idx} variant="caption" sx={{ display: 'block', color: '#bbb' }}>
                                      • {line}
                                    </Typography>
                                  ))}
                                  {/* Visual flow (ASCII) */}
                                  {exp.flow && exp.flow.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" sx={{ display: 'block', color: 'lime', mb: 0.5 }}>
                                        Request path (vertical):
                                      </Typography>
                                      <pre style={{ margin: 0, color: 'lime', background: 'transparent', fontFamily: 'monospace' }}>
                                        {buildAsciiFlow(exp.flow)}
                                      </pre>
                                      <Typography variant="caption" sx={{ display: 'block', color: 'lime', mt: 1, mb: 0.5 }}>
                                        Diagram (boxes):
                                      </Typography>
                                      <pre style={{ margin: 0, color: 'lime', background: 'transparent', fontFamily: 'monospace' }}>
                                        {buildAsciiBoxFlow(exp.flow)}
                                      </pre>
                                    </Box>
                                  )}
                                  {/* Practical takeaway */}
                                  {exp.summary && (
                                    <Typography variant="caption" sx={{ display: 'block', color: '#ffa726', mt: 0.5 }}>
                                      What this means: {exp.summary}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()}
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <Typography variant="body2" sx={{ color: 'lime' }}>
                              Equivalent curl command:
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => copyToClipboard(debugResult.curlCommand)}
                              startIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                              sx={{ 
                                color: 'lime', 
                                fontSize: '0.7rem',
                                minWidth: 'auto',
                                p: 0.5
                              }}
                            >
                              Copy
                            </Button>
                          </Box>
                          <pre style={{ margin: 0, color: 'lime', background: 'transparent' }}>
                            {debugResult.curlCommand}
                          </pre>
                          
                          <Typography variant="caption" sx={{ color: '#666', mt: 2, display: 'block' }}>
                            Fetched at: {new Date(debugResult.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  )}
                  
                  {/* Sitemap Scanner Results */}
                  {sitemapResult && (
                    <Paper sx={{ 
                      mt: 2,
                      p: 2, 
                      bgcolor: sitemapResult.found ? '#003d00' : '#2d1b1b', 
                      border: sitemapResult.found ? '1px solid cyan' : '1px solid #ff6b6b',
                      borderRadius: 1
                    }}>
                      {sitemapResult.found ? (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'cyan' }}>
                              ✅ Sitemap Found: {sitemapResult.sitemapUrl}
                              {sitemapResult.subpath && (
                                <Typography variant="caption" sx={{ color: 'orange', display: 'block' }}>
                                  📁 Using subpath: {sitemapResult.subpath}
                                </Typography>
                              )}
                              {sitemapResult.subSitemapsCount > 0 && (
                                <Typography variant="caption" sx={{ color: 'yellow', display: 'block' }}>
                                  📋 Processed {sitemapResult.subSitemapsCount} sub-sitemap{sitemapResult.subSitemapsCount !== 1 ? 's' : ''}
                                  {sitemapResult.failedCount > 0 && (
                                    <span style={{ color: '#ff6b6b' }}>
                                      {' '}• {sitemapResult.failedCount} failed
                                    </span>
                                  )}
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'lime' }}>
                              {sitemapResult.totalUrls} URLs found
                            </Typography>
                          </Box>
                          
                          {/* Failed Sitemaps Warning */}
                          {sitemapResult.failedSitemaps && sitemapResult.failedSitemaps.length > 0 && (
                            <Alert 
                              severity="warning" 
                              sx={{ 
                                mb: 2, 
                                bgcolor: 'rgba(255, 152, 0, 0.1)', 
                                color: 'orange',
                                '& .MuiAlert-icon': { color: 'orange' },
                                border: '1px solid orange'
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                ⚠️ {sitemapResult.failedCount} Sub-sitemap{sitemapResult.failedCount !== 1 ? 's' : ''} Failed:
                              </Typography>
                              {sitemapResult.failedSitemaps.slice(0, 3).map((failed, index) => (
                                <Typography key={index} variant="caption" sx={{ display: 'block', ml: 1, color: '#ffcc80' }}>
                                  • {failed.originalUrl.split('/').pop()} - {
                                    failed.reason === 'www_mismatch' ? 'Contains www. but domain doesn\'t support it' :
                                    failed.reason === 'missing_www' ? 'Missing www. prefix that domain requires' :
                                    'Server unreachable'
                                  }
                                </Typography>
                              ))}
                              {sitemapResult.failedCount > 3 && (
                                <Typography variant="caption" sx={{ display: 'block', ml: 1, color: '#666' }}>
                                  ... and {sitemapResult.failedCount - 3} more
                                </Typography>
                              )}
                            </Alert>
                          )}
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'yellow' }}>
                              PAGE URLS FROM SITEMAP:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                onClick={exportToCSV}
                                startIcon={<FileDownloadIcon sx={{ fontSize: 12 }} />}
                                sx={{ 
                                  color: 'lime', 
                                  fontSize: '0.7rem',
                                  minWidth: 'auto',
                                  p: 0.5,
                                  '&:hover': { color: 'lightgreen' }
                                }}
                              >
                                Export CSV
                              </Button>
                              <Button
                                size="small"
                                onClick={() => setShowSitemapUrls(!showSitemapUrls)}
                                startIcon={<ListIcon sx={{ fontSize: 12 }} />}
                                sx={{ 
                                  color: 'cyan', 
                                  fontSize: '0.7rem',
                                  minWidth: 'auto',
                                  p: 0.5
                                }}
                              >
                                {showSitemapUrls ? 'Hide URLs' : 'Show URLs'}
                              </Button>
                            </Box>
                          </Box>
                          
                          {showSitemapUrls && (
                            <Box sx={{ 
                              maxHeight: '300px', 
                              overflowY: 'auto',
                              border: '1px solid #444',
                              borderRadius: 1,
                              p: 1,
                              bgcolor: '#000'
                            }}>
                              {sitemapResult.urls?.map((urlData, index) => (
                                <Box 
                                  key={index} 
                                  sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    mb: 0.5,
                                    p: 0.5,
                                    borderRadius: 0.5,
                                    '&:hover': { bgcolor: '#1a1a1a' },
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => applySitemapUrl(urlData.path)}
                                >
                                  <Typography sx={{ 
                                    color: 'lime',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    flex: 1
                                  }}>
                                    {urlData.path}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        visitPage(urlData.path);
                                      }}
                                      startIcon={<OpenInNewIcon sx={{ fontSize: 10 }} />}
                                      sx={{ 
                                        color: 'orange', 
                                        fontSize: '0.6rem',
                                        minWidth: 'auto',
                                        p: 0.25,
                                        '&:hover': { color: 'gold' }
                                      }}
                                    >
                                      Visit
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        applySitemapUrl(urlData.path);
                                      }}
                                      startIcon={<LaunchIcon sx={{ fontSize: 10 }} />}
                                      sx={{ 
                                        color: 'cyan', 
                                        fontSize: '0.6rem',
                                        minWidth: 'auto',
                                        p: 0.25
                                      }}
                                    >
                                      Use
                                    </Button>
                                  </Box>
                                </Box>
                              ))}
                              
                              {sitemapResult.totalUrls > 100 && (
                                <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
                                  Showing first 100 URLs out of {sitemapResult.totalUrls} total
                                </Typography>
                              )}
                            </Box>
                          )}
                          
                          <Typography variant="caption" sx={{ color: '#666', mt: 2, display: 'block' }}>
                            Click any URL to use it for purging • Use "Visit" to open page in new tab • Use "Export CSV" to download all URLs
                            <br />Scanned at: {new Date(sitemapResult.scannedAt).toLocaleString()}
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#ff6b6b', mb: 1 }}>
                            ❌ No sitemap found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#888', mb: 1 }}>
                            {sitemapResult.message || sitemapResult.error}
                          </Typography>
                          {sitemapResult.triedUrls && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                Tried locations:
                              </Typography>
                              {sitemapResult.triedUrls.map((url, index) => (
                                <Typography key={index} variant="caption" sx={{ color: '#555', display: 'block', ml: 1 }}>
                                  • {url}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Paper>
                  )}
                </Paper>
              )}

              {/* What the edge is serving after the purge. The purge API
                  returning 200 only means Fastly accepted the request -- it
                  says nothing about whether the origin had regenerated yet,
                  which is the failure this panel exists to make visible. */}
              {purgeVerification && (
                <Paper sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: purgeVerification.timedOut
                    ? '#ffa726'
                    : (purgeVerification.pages || []).some((p) => p.stillStale)
                      ? '#ff6b6b'
                      : 'cyan'
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Cache verification
                  </Typography>

                  {!purgeVerification.performed ? (
                    <Typography variant="body2" sx={{ color: '#888' }}>
                      {purgeVerification.reason === 'too-many-urls'
                        ? 'Too many URLs to verify individually — purged without checking the result.'
                        : 'Whole-domain purge — no single page to verify.'}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                        {purgeVerification.message}
                      </Typography>

                      {purgeVerification.sawStale && (
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                          Origin was mid-regeneration; waited {Math.round((purgeVerification.waitedMs || 0) / 1000)}s
                          across {purgeVerification.polls} check{purgeVerification.polls === 1 ? '' : 's'}.
                        </Typography>
                      )}

                      {(purgeVerification.pages || []).map((page) => (
                        <Box key={page.url} sx={{ mt: 1.5 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {page.url}
                          </Typography>
                          {page.after && page.after.ok ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              <Chip
                                size="small"
                                label={page.stillStale ? 'origin: STALE' : `origin: ${page.after.nextjsCache || 'n/a'}`}
                                color={page.stillStale ? 'error' : 'success'}
                                variant="outlined"
                              />
                              {page.contentChanged === true && (
                                <Chip size="small" label="content updated" color="success" variant="outlined" />
                              )}
                              {page.contentChanged === false && (
                                <Chip size="small" label="content unchanged" variant="outlined" />
                              )}
                              {page.after.contentModified && (
                                <Chip size="small" label={`modified: ${page.after.contentModified}`} variant="outlined" />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#ff6b6b' }}>
                              Could not check this page{page.after?.error ? `: ${page.after.error}` : ''} — the purge itself still succeeded.
                            </Typography>
                          )}
                        </Box>
                      ))}

                      {(purgeVerification.pages || []).some((p) => p.contentChanged === false && !p.stillStale) && (
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5 }}>
                          "Content unchanged" is expected if nothing was published since the last purge.
                        </Typography>
                      )}
                    </>
                  )}
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity & Purges Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Recent Activity
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    console.log('Refresh button clicked - loading both functions');
                    loadRecentActivity();
                    loadRecentPurges();
                  }}
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </Button>
              </Box>
              
              {recentActivity.length === 0 && recentPurges.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent activity
                </Typography>
              ) : (
                <List dense>
                  {/* Show purge operations first with Purge Again buttons */}
                  {recentPurges.slice(0, 6).map((purge, index) => (
                    <React.Fragment key={`purge-${purge.id}`}>
                      <ListItem>
                        <ListItemIcon>
                          {getStatusIcon(purge.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getPurgeTypeIcon(purge.action)}
                              <Typography variant="body2">
                                {purge.action?.replace('_', ' ')} - {purge.site_name}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {purge.user_name} • {new Date(purge.created_at).toLocaleString()}
                              </Typography>
                              {purge.target && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Target: {purge.target}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleQuickPurge(purge)}
                          disabled={quickPurgingId === purge.id}
                          startIcon={quickPurgingId === purge.id ? <CircularProgress size={12} /> : <RefreshIcon />}
                          title={`Execute purge again: ${purge.action?.replace('_', ' ')} - ${purge.target || 'all content'}`}
                          sx={{ 
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            fontSize: '0.7rem',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white'
                            }
                          }}
                        >
                          {quickPurgingId === purge.id ? 'Purging...' : 'Purge Again'}
                        </Button>
                      </ListItem>
                      {index < recentPurges.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                  
                  {/* Show other activity (non-purge operations) */}
                  {recentActivity.filter(log => !['purge_url', 'purge_surrogate_key', 'purge_all'].includes(log.action)).slice(0, 4).map((log, index) => (
                    <React.Fragment key={`activity-${log.id}`}>
                      <ListItem>
                        <ListItemIcon>
                          {getStatusIcon(log.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getPurgeTypeIcon(log.action)}
                              <Typography variant="body2">
                                {log.action?.replace('_', ' ')} - {log.site_name}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {log.user_name} • {new Date(log.created_at).toLocaleString()}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < Math.min(recentActivity.filter(log => !['purge_url', 'purge_surrogate_key', 'purge_all'].includes(log.action)).length - 1, 3) && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
              
              {(recentPurges.length > 6 || recentActivity.length > 4) && (
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={() => {
                    loadRecentActivity();
                    loadRecentPurges();
                  }}
                >
                  Load More
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, purgeLog: null, title: '', message: '' })}
        aria-labelledby="confirm-purge-dialog-title"
        aria-describedby="confirm-purge-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-purge-dialog-title" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: 'primary.main'
        }}>
          <RefreshIcon />
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-purge-dialog-description" sx={{ whiteSpace: 'pre-line' }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setConfirmDialog({ open: false, purgeLog: null, title: '', message: '' })}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmPurge}
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            autoFocus
          >
            Purge Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* Whole-domain purge confirmation */}
      <Dialog
        open={wholeDomainDialog.open}
        onClose={() => setWholeDomainDialog((prev) => ({ ...prev, open: false }))}
        aria-labelledby="whole-domain-purge-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="whole-domain-purge-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}
        >
          <WarningIcon />
          Purge an entire domain?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {wholeDomainDialog.wholeDomainKeys.length === 1
              ? 'This entry has no path, so it matches the site-wide key and will purge every cached page on the domain:'
              : 'These entries have no path, so they match site-wide keys and will purge every cached page on those domains:'}
          </DialogContentText>
          <Box sx={{ mt: 2, mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            {wholeDomainDialog.wholeDomainKeys.map((key) => (
              <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace' }}>
                {key}
              </Typography>
            ))}
          </Box>
          <DialogContentText>
            To purge just the homepage instead, cancel and add a trailing slash
            (for example <code>{`${wholeDomainDialog.wholeDomainKeys[0] || 'example.com'}/`}</code>).
            Whole-domain purges are rate limited to one every 5 minutes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setWholeDomainDialog((prev) => ({ ...prev, open: false }))}
            variant="outlined"
            color="inherit"
            autoFocus
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmWholeDomainPurge}
            variant="contained"
            color="warning"
            startIcon={<WarningIcon />}
          >
            Purge Entire Domain
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};

export default Dashboard;
